import { FormikConfig, FormikContextType, FormikHelpers, useFormik } from 'formik'
import { ReactNode, useEffect, useMemo } from 'react'
import { SchemaOf } from 'yup'

import { MaybePromise } from '../../../types/utility'

import { EmbroideryService } from './EmbroideryService'
import { EmbroideryFormKind, EmbroideryFormOptions, EmbroideryFormValues } from './types'

type UseEmbroideryFormOpts<T extends keyof EmbroideryFormValues> = {
  /* The validation schema that should be applied for the current form. Must only enforce validation on the fields that exist for the provided embroidery kind form. */
  validationSchema?: SchemaOf<Partial<EmbroideryFormValues[T]>>
  /* Augment the initial form values. Provided with the default sticky/initial form values for the form. */
  createInitialValues?: (initialValues: EmbroideryFormValues[T]) => EmbroideryFormValues[T]
  /* Create the embroidery options that should be displayed for the given embroidery form. */
  createEmbroideryOptions: (
    form: EmbroideryFormValues[T],
    helpers: FormikHelpers<EmbroideryFormValues[T]>
  ) => MaybePromise<EmbroideryFormOptions>
  /* Render the preview item that should be displayed for the given embroidery form. */
  previewItem?: (form: FormikContextType<EmbroideryFormValues[T]>) => ReactNode
  /* Define which form kind should be set after submission. Defaults to null. */
  finalFormKind?: EmbroideryFormKind
  /* Callback function that runs after form submission. */
  onSubmitCompleted?: (values: EmbroideryFormValues[T]) => void
} & Omit<FormikConfig<EmbroideryFormValues[T]>, 'initialValues' | 'onSubmit' | 'validationSchema'>

/** Initialize the form state for a given embroidery form. */
export function useEmbroideryForm<T extends keyof EmbroideryFormValues>(
  kind: T,
  {
    validationSchema,
    createInitialValues,
    createEmbroideryOptions,
    previewItem,
    finalFormKind,
    onSubmitCompleted,
    ...rest
  }: UseEmbroideryFormOpts<T>
) {
  const {
    stickyFormValuesByKind,
    setStickyFormValuesByKind,
    setSelectedFormKind,
    setPreviewItemsByKind,
    setSelectedEmbroideryOptions,
    initialFormValuesByKind,
  } = EmbroideryService.useContainer()

  // Derive the initial form values based on the sticky form values for the given embroidery kind.
  // If `createInitialValues` is provided, use it to augment the initial form values applied to the form.
  const initialFormValues = useMemo(() => {
    const initialFormStateValues = stickyFormValuesByKind[kind] ?? initialFormValuesByKind[kind]
    return createInitialValues
      ? createInitialValues(initialFormStateValues)
      : initialFormStateValues
  }, [stickyFormValuesByKind, initialFormValuesByKind, kind, createInitialValues])

  // Initialize the formik form object
  const form = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    async onSubmit(values, helpers) {
      const selectedEmbroideryOptions = await createEmbroideryOptions(values, helpers)
      setStickyFormValuesByKind(prev => ({
        ...prev,
        [kind]: values,
      }))
      setSelectedEmbroideryOptions(prev => ({
        ...prev,
        [kind]: selectedEmbroideryOptions,
      }))
      setSelectedFormKind(finalFormKind ?? null)
      if (onSubmitCompleted) {
        onSubmitCompleted(values)
      }
    },
    ...rest,
  })

  // Set the preview item for the given embroidery kind
  useEffect(() => {
    if (previewItem) {
      setPreviewItemsByKind(prev => ({
        ...prev,
        [kind]: [previewItem(form)],
      }))
    }
    // We only want to update the preview item when form values are changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values, kind])

  // Reset the current embroidery kind form to its initial state
  const resetForm = () => {
    const defaultFormValues = createInitialValues
      ? createInitialValues(initialFormValuesByKind[kind])
      : initialFormValuesByKind[kind]
    form.setValues(defaultFormValues)
    setStickyFormValuesByKind(prev => ({
      ...prev,
      [kind]: defaultFormValues,
    }))
    setSelectedEmbroideryOptions(prev => ({
      ...prev,
      [kind]: null,
    }))
  }

  return { form, resetForm }
}
